// Customer portal PDF viewer (Session AGH). A "View" button opens the document in place instead of
// downloading it, so a customer who wants to read ELE's RAMS on a phone can just read it.
//
// WHY AN IFRAME AND NOT PDF.js. The browser's own PDF viewer needs no library, no bundle weight and no
// worker, and it handles zoom, search and printing for free. The document is served by the existing
// authenticated proxy, which now sends Content-Disposition: inline, so the frame simply renders it.
//
// THE iOS SAFARI PROBLEM, and why the fallback is not decorative. iOS Safari will not reliably render a
// PDF inside an iframe: depending on version it shows the first page only, or a blank frame. The portal
// is used on phones, so a viewer that quietly showed nothing would be worse than the download link it
// replaced. Every viewer therefore carries TWO always-visible alternatives, never hidden behind a
// failure that cannot be detected from script: "Open in a new tab" (which uses the browser's full-page
// PDF handling and works everywhere, iOS included) and "Download". A blank frame is then a nuisance
// rather than a dead end, and the customer is never left with nothing to press.
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface PortalDocumentViewerProps {
  /** The authenticated proxy URL for the document (inline by default). */
  href: string;
  /** The document's display name, shown in the header and read by assistive technology. */
  title: string;
  onClose: () => void;
}

// `?download=true` on the same proxy URL forces Content-Disposition: attachment (documentDisposition).
export function downloadHref(href: string): string {
  return href.includes("?") ? `${href}&download=true` : `${href}?download=true`;
}

export function PortalDocumentViewer({ href, title, onClose }: PortalDocumentViewerProps) {
  const closeRef = useRef<HTMLButtonElement | null>(null);

  // onClose held in a ref so the open effect below depends on NOTHING and runs exactly once per open.
  // Callers pass a fresh inline arrow, and a parent re-render would otherwise tear the effect down and
  // re-run it mid-read, pulling focus back to the panel (the same trap the engineer viewer documents).
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Open/close housekeeping, mirroring the engineer viewer: focus moves in, the page behind is marked
  // inert (which is what makes the aria-modal below TRUE rather than merely asserted, and stops Tab
  // reaching the obscured document rows and stacking a second dialog), body scroll is locked so
  // scrolling past the end of the PDF does not chain to the page behind, and focus is RESTORED to
  // whatever opened it, so a keyboard customer does not restart at the top of a twelve-row list.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    window.addEventListener("keydown", onKey);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const root = document.getElementById("root");
    const hadInert = root?.hasAttribute("inert") ?? false;
    if (root && !hadInert) root.setAttribute("inert", "");

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      if (root && !hadInert) root.removeAttribute("inert");
      previouslyFocused?.focus?.();
    };
  }, []);

  // Portaled to <body> because the effect above marks #root inert: a dialog rendered INSIDE the inert
  // subtree would make its own controls unclickable.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/70 p-0 sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Full screen on a phone (100vw/100vh via inset-0 + h-full), a framed panel from sm up. */}
      <div
        className="flex h-full w-full flex-col overflow-hidden bg-white sm:h-[90vh] sm:max-w-4xl sm:rounded-ele sm:border sm:border-white/10"
      >
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
          <h2 className="min-w-0 flex-1 truncate font-heading text-lg text-ele-white">{title}</h2>
          <div className="flex flex-none items-center gap-3">
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="min-h-11 font-menu text-xs tracking-wide leading-[44px] text-ele-blue hover:underline"
            >
              Open in a new tab
            </a>
            <a
              href={downloadHref(href)}
              className="min-h-11 font-menu text-xs tracking-wide leading-[44px] text-ele-green hover:underline"
            >
              Download
            </a>
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              aria-label={`Close ${title}`}
              className="min-h-11 min-w-11 rounded-ele border border-white/15 px-3 font-menu text-xs tracking-wide text-ele-body hover:border-ele-purple hover:text-ele-white"
            >
              Close
            </button>
          </div>
        </header>
        <div data-vscroll className="min-h-0 flex-1 overflow-auto bg-white">
          <iframe src={href} title={title} className="h-full w-full border-0" />
        </div>
        <p className="border-t border-white/10 px-4 py-2 text-xs text-ele-body">
          If the document does not appear, use Open in a new tab, or call the office on 0203 576 4760.
        </p>
      </div>
    </div>,
    document.body,
  );
}

// A document row's "View" control, owning its own open state.
//
// Self-contained deliberately: the document rows live in several separately-rendered panels (the
// static library, the company documents, a site's own documents, the supplementary-test annexes), and
// threading one shared "which document is open" value through all of them is how two rows end up
// crossing (the Session AFI per-row lesson). Each button opens and closes only its own viewer.
export function PortalViewDocumentButton({ href, title }: { href: string; title: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`View ${title}`}
        className="min-h-11 font-menu text-xs tracking-wide text-ele-green hover:underline"
      >
        View
      </button>
      {open && <PortalDocumentViewer href={href} title={title} onClose={() => setOpen(false)} />}
    </>
  );
}
